import { TYPES } from "@/types";
import { myContainer } from "@/inversify.config";
import { IEmploymentService } from "@/interfaces/IEmploymentService";
import * as dtos from "@/models/dtos";

import _ from "lodash";
import { ToastNotification } from "@/models/ToastNotification";
const client = myContainer.get<IEmploymentService>(TYPES.IEmploymentService);

export default {
  state: {
    containerSkills: [] as dtos.SkillCategory[],
    skillCollections: [] as dtos.SkillCollection[],
    positionSkills: [] as dtos.SkillCategory[],
    skillToEdit: {} as dtos.EditableSkill,
    skillCollectionToEdit: {} as dtos.SkillCollection,
    editName: false,
    editDescription: false,
    icons: [] as dtos.ImageResponse[],
  },

  getters: {
    containerSkills: (state: any) => state.containerSkills,
    skillCollections: (state: any) => state.skillCollections,
    skill: (state: any) => (id: number) =>
      _.find(state.containerSkills, (px) => px.id === id),
    positionSkills: (state: any) => state.positionSkills,
    skillToEdit: (state: any) => state.skillToEdit,
    skillCollectionToEdit: (state: any) => state.skillCollectionToEdit,
    icons: (state: any) => state.icons,
  },

  mutations: {
    setContainerSkills(state: any, skills: any) {
      state.containerSkills = skills;
    },
    setPositionSkills(state: any, value: any) {
      state.positionSkills = value;
    },
    setSkillCollections(state: any, value: any) {
      state.skillCollections = value;
    },
    setSkillCollectionToEdit(state: any, value: any) {
      state.skillCollectionToEdit = value;
    },
    updateSkillCollection(
      state: any,
      options: {
        keyToUpdate: "all" | "name" | "description" | "icon" | "skills";
        value: any;
      }
    ) {
      const { keyToUpdate, value } = options;
      if (keyToUpdate === "all") {
        state.skillCollectionToEdit = value;
        return;
      }

      state.skillCollectionToEdit[keyToUpdate] = value;
    },
    addSkillToCollection(state: any, skill: dtos.Skill) {
      const skillCollection = state.skillCollectionToEdit;
      skillCollection.skills.push(skill);
    },
    removeSkillFromCollection(state: any, skillId: string) {
      const skillCollection = state.skillCollectionToEdit;
      const skillIndex = skillCollection.skills.findIndex(
        (s: any) => s.id === skillId
      );
      skillCollection.skills.splice(skillIndex, 1);
    },
    setSkillToEdit(
      state: any,
      value: {
        skill: dtos.Skill;
        categoryId: string | null;
      } | null
    ) {
      if (!value) {
        state.skillToEdit = null;
        return;
      }
      const editableSkill = {
        ...value.skill,
        categoryId: value.categoryId,
      } as dtos.EditableSkill;

      state.skillToEdit = editableSkill;
    },
    updateSkill(
      state: any,
      options: {
        keyToUpdate:
          | "all"
          | "name"
          | "description"
          | "categoryId"
          | "icon"
          | "questions";
        value: any;
      }
    ) {
      const { keyToUpdate, value } = options;
      if (keyToUpdate === "all") {
        state.skillToEdit = value;
        return;
      }
      state.skillToEdit[keyToUpdate] = value;
    },
    updateQuestion(
      state: any,
      value: {
        questionId: string;
        newQuestion: string;
      }
    ) {
      const question = state.skillToEdit.questions.find(
        (q: any) => q.id === value.questionId
      );

      if (question) {
        question.question = value.newQuestion;
      }
      return;
    },
    setIcons(state: any, icons: dtos.ImageResponse[]) {
      state.icons = icons;
    },
  },
  actions: {
    async upsertSkillCollection({ commit, dispatch, getters }: any) {
      const payload = {
        id: getters.skillCollectionToEdit?.id || "",
        containerId: getters.container.id,
        name: getters.skillCollectionToEdit?.name,
        description: getters.skillCollectionToEdit?.description,
        icon: getters.skillCollectionToEdit?.icon,
        skillIds: getters.skillCollectionToEdit?.skills.map(
          (s: dtos.Skill) => s.id
        ),
      } as dtos.UpsertSkillCollection;

      try {
        await client.Container.upsertSkillCollection(payload);
        dispatch(
          "addNotification",
          new ToastNotification("success", `Kompetenssamling sparad.`)
        );
      } catch (error) {
        dispatch(
          "addNotification",
          new ToastNotification("alert", `Något gick fel.`)
        );
      } finally {
        await dispatch("loadSkillCollections");
      }
    },
    async deleteSkillCollection({ dispatch, getters }: any, id: string) {
      // remove above and uncomment below when API-endpoint is ready
      try {
        await client.Container.deleteSkillCollection({
          containerId: getters.container.id,
          id,
        });
        dispatch(
          "addNotification",
          new ToastNotification("success", `Kompetenssamling borttagen.`)
        );
      } catch (error) {
        dispatch(
          "addNotification",
          new ToastNotification("alert", `Något gick fel.`)
        );
      } finally {
        await dispatch("loadSkillCollections");
      }
    },
    async loadSkillCollections({ commit, getters }: any) {
      const response = await client.Container.getSkillCollections(
        getters.container.id
      );
      commit("setSkillCollections", response.data);
    },
    async upsertSkill(
      { dispatch, getters }: any,
      value: {
        skill: dtos.Skill;
        categoryId: string;
      }
    ) {
      const skillWithoutNewQuestionIds = {
        ...value.skill,
        questions: value.skill.questions.map((q) => {
          return {
            question: q.question,
            id: q.id.includes("new_question-") ? "" : q.id,
          };
        }),
      };

      const payload = {
        containerId: getters.container.id,
        categoryId: value.categoryId,
        skill: skillWithoutNewQuestionIds,
      };
      try {
        await client.Container.upsertSkill(payload);
        dispatch(
          "addNotification",
          new ToastNotification("success", `Kompetens sparad.`)
        );
      } catch (error) {
        dispatch(
          "addNotification",
          new ToastNotification("alert", `Något gick fel.`)
        );
      } finally {
        await dispatch("loadContainerSkills");
      }
    },
    async deleteSkill({ dispatch, getters }: any, skillId: string) {
      try {
        await client.Container.deleteSkill({
          containerId: getters.container.id,
          id: skillId,
        });
        dispatch(
          "addNotification",
          new ToastNotification("success", `Kompetens borttagen.`)
        );
      } catch (error) {
        dispatch(
          "addNotification",
          new ToastNotification("alert", `Något gick fel.`)
        );
      } finally {
        await dispatch("loadContainerSkills");
      }
    },
    async loadContainerSkills({ commit, getters }: any) {
      const response = await client.Container.getCompetences(
        getters.container.id
      );
      commit("setContainerSkills", response.data);
    },
    async loadPositionSkills({ getters, commit }: any, positionId: number) {
      const response = await client.JobPosition.getPositionSkills(
        getters.container.id,
        positionId
      );

      commit("setPositionSkills", response);
    },
    async setPositionSkills({ commit }: any, value: dtos.SkillCategory[]) {
      commit("setPositionSkills", value);
    },
    async addPositionSkill(
      { getters, dispatch }: any,
      { positionId, skillId }: { positionId: number; skillId: string }
    ) {
      await client.JobPosition.addPositionSkill(
        getters.container.id,
        positionId,
        skillId
      );

      dispatch("loadPositionSkills", positionId);
    },
    async removePositionSkill(
      { getters, dispatch }: any,
      { positionId, skillId }: { positionId: number; skillId: string }
    ) {
      await client.JobPosition.removePositionSkill(
        getters.container.id,
        positionId,
        skillId
      );

      dispatch("loadPositionSkills", positionId);
    },
    updateSkillInStore(
      { commit }: any,
      options: {
        keyToUpdate:
          | "all"
          | "name"
          | "description"
          | "categoryId"
          | "icon"
          | "questions";
        value: any;
      }
    ) {
      commit("updateSkill", options);
    },
    updateSkillCollectionInStore(
      { commit }: any,
      options: {
        keyToUpdate: "all" | "name" | "description" | "skills";
        value: any;
      }
    ) {
      commit("updateSkillCollection", options);
    },
    async fetchIcons({ commit, getters }: any) {
      const response = await client.Container.getIcons(getters.container.id);

      commit("setIcons", response.data);
      return response.data;
    },
    resetSkillCollectionToEdit({ commit }: any) {
      commit("setSkillCollectionToEdit", {});
    },
  },
};
